import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { History, Loader2, ArrowRight, User, MessageSquare, FileEdit, FilePlus, Send, CheckCircle2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
    nouveau: 'Nouveau',
    sans_reponse_1: 'Sans réponse 1',
    sans_reponse_2: 'Sans réponse 2',
    sans_reponse_3: 'Sans réponse 3',
    a_rappeler: 'À rappeler',
    visite_virtuelle: 'Visite virtuelle',
    visite_presentiel: 'Visite présentielle',
    visite_presentielle: 'Visite présentielle',
    devis_estimatif: 'Devis estimatif',
    listing_attente: 'Listing en attente',
    listing_telephone: 'Listing téléphone',
    listing_email: 'Listing email',
    relance_whatsapp: 'Relance WhatsApp',
    faux_contact: 'Faux contact',
    non_interessant: 'Non intéressant',
    accepte: 'Accepté',
    refuse: 'Refusé',
};

const STATUT_COLORS = {
    nouveau: 'bg-blue-50 text-blue-700 border-blue-200',
    sans_reponse_1: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    sans_reponse_2: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    sans_reponse_3: 'bg-orange-50 text-orange-700 border-orange-200',
    a_rappeler: 'bg-purple-50 text-purple-700 border-purple-200',
    visite_virtuelle: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    visite_presentiel: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    visite_presentielle: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    devis_estimatif: 'bg-teal-50 text-teal-700 border-teal-200',
    listing_attente: 'bg-gray-100 text-gray-700 border-gray-200',
    listing_telephone: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    listing_email: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    relance_whatsapp: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    faux_contact: 'bg-red-50 text-red-700 border-red-200',
    non_interessant: 'bg-gray-100 text-gray-600 border-gray-200',
    accepte: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    refuse: 'bg-red-50 text-red-700 border-red-200',
};

function StatutBadge({ value }) {
    if (!value) return <span className="text-xs text-gray-400 italic">—</span>;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUT_COLORS[value] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
            {STATUT_LABELS[value] || value}
        </span>
    );
}

function fmt(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

const ACTION_META = {
    devis_created: { label: 'Devis créé', icon: FilePlus, accent: 'text-[#1B365D]' },
    statut_change: { label: 'Statut changé', icon: FileEdit, accent: 'text-blue-600' },
    formule_created: { label: 'Formule créée', icon: FilePlus, accent: 'text-emerald-600' },
    formule_updated: { label: 'Formule modifiée', icon: FileEdit, accent: 'text-amber-600' },
    devis_sent: { label: 'Devis envoyé', icon: Send, accent: 'text-sky-600' },
    devis_sent_multiple: { label: 'Devis envoyés (multi)', icon: Send, accent: 'text-sky-600' },
    devis_accepted: { label: 'Devis accepté', icon: CheckCircle2, accent: 'text-emerald-600' },
};

function fmtCurrency(v) {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

const FIELD_LABELS = {
    total_ttc: 'Total TTC',
    total_ht: 'Total HT',
    tva: 'TVA',
    distance_km: 'Distance (km)',
    prix_prestation: 'Prix prestation',
    date_chargement: 'Date chargement',
    date_dechargement: 'Date déchargement',
    adresse_depart: 'Adresse de départ',
    adresse_arrivee: 'Adresse d\'arrivée',
    nom: 'Nom client',
    telephone: 'Téléphone',
    email: 'Email',
    etage_depart: 'Étage départ',
    etage_arrivee: 'Étage arrivée',
    ascenseur_depart: 'Ascenseur départ',
    ascenseur_arrivee: 'Ascenseur arrivée',
    portage_depart: 'Portage départ',
    portage_arrivee: 'Portage arrivée',
    monte_meuble_depart: 'Monte-meuble départ',
    monte_meuble_arrivee: 'Monte-meuble arrivée',
    aot_depart: 'AOT départ',
    aot_arrivee: 'AOT arrivée',
    garde_meuble: 'Garde-meuble',
    garde_meuble_duree: 'Garde-meuble (durée)',
    garde_meuble_mensualite: 'Garde-meuble (mensualité)',
    cartons_standards: 'Cartons standards',
    cartons_livres: 'Cartons livres',
    cartons_verres: 'Cartons verres',
    cartons_bouteilles: 'Cartons bouteilles',
    bulle_10m: 'Bulle 10m',
    bulle_100m: 'Bulle 100m',
    adhesif: 'Adhésif',
    fournitures: 'Fournitures',
    autre_fourniture: 'Autre fourniture',
    remarques: 'Remarques',
    prestations: 'Prestations supp.',
    prestations_count: 'Nb prestations supp.',
};

const MONEY_FIELDS = new Set(['total_ttc', 'total_ht', 'tva', 'prix_prestation', 'garde_meuble_mensualite']);
const BOOL_FIELDS = new Set(['ascenseur_depart', 'ascenseur_arrivee', 'aot_depart', 'aot_arrivee', 'monte_meuble_depart', 'monte_meuble_arrivee', 'garde_meuble']);

function fmtFieldValue(field, value) {
    if (value === null || value === undefined || value === '') return '—';
    if (MONEY_FIELDS.has(field)) return fmtCurrency(value);
    if (BOOL_FIELDS.has(field)) return value ? 'Oui' : 'Non';
    if (typeof value === 'object') {
        if (Array.isArray(value)) return `${value.length} élément${value.length > 1 ? 's' : ''}`;
        try { return JSON.stringify(value); } catch { return String(value); }
    }
    return String(value);
}

function DevisActionDetails({ action, details }) {
    if (!details || Object.keys(details).length === 0) return null;
    if (action === 'statut_change') {
        return (
            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                <StatutBadge value={details.from} />
                <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                <StatutBadge value={details.to} />
                {details.refuse_reason && <span className="text-red-600 italic">— {details.refuse_reason}</span>}
                {details.remark && <span className="text-gray-600 italic">— {details.remark}</span>}
            </div>
        );
    }
    if (action === 'formule_created') {
        return (
            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-600">
                <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-semibold">{details.formule}</span>
                <span>· {details.numero}</span>
                <span>· {fmtCurrency(details.total_ttc)}</span>
                {details.volume && <span>· {details.volume} m³</span>}
                {details.distance_km && <span>· {details.distance_km} km</span>}
            </div>
        );
    }
    if (action === 'formule_updated') {
        const c = details.changes || {};
        // Special render for prestations array (show added/removed items)
        const prestationsDiff = c.prestations;
        const otherEntries = Object.entries(c).filter(([k]) => k !== 'prestations' && k !== 'prestations_count');

        const lines = otherEntries.map(([k, v]) => {
            const label = FIELD_LABELS[k] || k;
            const fromV = fmtFieldValue(k, v.from);
            const toV = fmtFieldValue(k, v.to);
            return (
                <div key={k} className="text-xs text-gray-600 flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-gray-700">{label}:</span>
                    <span className="line-through text-gray-400">{fromV}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <span className="text-[#1B365D] font-medium">{toV}</span>
                </div>
            );
        });

        const renderPrestations = () => {
            if (!prestationsDiff) return null;
            const oldList = prestationsDiff.from || [];
            const newList = prestationsDiff.to || [];
            const oldKeys = new Set(oldList.map(p => `${p.libelle}|${p.prix}`));
            const newKeys = new Set(newList.map(p => `${p.libelle}|${p.prix}`));
            const added = newList.filter(p => !oldKeys.has(`${p.libelle}|${p.prix}`));
            const removed = oldList.filter(p => !newKeys.has(`${p.libelle}|${p.prix}`));
            return (
                <div className="text-xs text-gray-600 space-y-1">
                    <div className="font-semibold text-gray-700">Prestations supp.:</div>
                    {removed.map((p, i) => (
                        <div key={`r${i}`} className="flex items-center gap-1.5 ml-2">
                            <span className="text-red-500 font-bold">−</span>
                            <span className="line-through text-gray-400">{p.libelle || '—'} ({fmtCurrency(p.prix)})</span>
                        </div>
                    ))}
                    {added.map((p, i) => (
                        <div key={`a${i}`} className="flex items-center gap-1.5 ml-2">
                            <span className="text-emerald-500 font-bold">+</span>
                            <span className="text-[#1B365D] font-medium">{p.libelle || '—'} ({fmtCurrency(p.prix)})</span>
                        </div>
                    ))}
                </div>
            );
        };

        const hasAnyChange = lines.length > 0 || prestationsDiff;
        return (
            <div className="mt-1 space-y-1">
                <div className="text-xs text-gray-500">
                    <span className="font-semibold">{details.formule}</span> · v{details.version} · {details.numero_old || '—'} → {details.numero_new}
                </div>
                {lines}
                {renderPrestations()}
                {!hasAnyChange && <span className="text-xs text-gray-400 italic">Aucune modification détectée</span>}
            </div>
        );
    }
    if (action === 'devis_sent' || action === 'devis_sent_multiple') {
        return (
            <div className="mt-1 text-xs text-gray-600 flex items-center gap-2 flex-wrap">
                <Send className="w-3 h-3 text-sky-500" />
                <span className="font-medium text-[#1B365D]">{details.to_email}</span>
                {details.formule && <span>· {details.formule}</span>}
                {details.numero && <span>· {details.numero}</span>}
                {details.count && <span className="bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded">{details.count} formules</span>}
            </div>
        );
    }
    if (action === 'devis_accepted') {
        return (
            <div className="mt-1 text-xs text-gray-600 flex items-center gap-2 flex-wrap">
                <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-semibold">{details.formule}</span>
                <span>· {details.numero}</span>
                <span>· {fmtCurrency(details.total_ttc)}</span>
            </div>
        );
    }
    return null;
}

export default function HistoriqueModal({ open, onOpenChange, prospectId, title }) {
    const [data, setData] = useState({ nom: '', history: [] });
    const [devisHist, setDevisHist] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('prospect');

    useEffect(() => {
        if (!open || !prospectId) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            axios.get(`${API_URL}/api/prospects/${prospectId}/history`, { withCredentials: true }).then(r => r.data).catch(() => ({ nom: '', history: [] })),
            axios.get(`${API_URL}/api/devis/by-prospect/${prospectId}/history`, { withCredentials: true }).then(r => r.data).catch(() => ({ history: [] })),
        ]).then(([prospect, devis]) => {
            if (cancelled) return;
            setData(prospect || { nom: '', history: [] });
            setDevisHist((devis && devis.history) || []);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, prospectId]);

    const items = [...(data.history || [])].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
    const devisItems = [...devisHist].sort((a, b) => new Date(b.at) - new Date(a.at));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="historique-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <History className="w-5 h-5 text-[#C9A962]" />
                        Historique — {title || data.nom || '—'}
                    </DialogTitle>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-md w-fit mt-1" data-testid="historique-tabs">
                    <button
                        onClick={() => setTab('prospect')}
                        className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${tab === 'prospect' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                        data-testid="tab-prospect"
                    >
                        Statuts prospect <span className="text-gray-400">({items.length})</span>
                    </button>
                    <button
                        onClick={() => setTab('devis')}
                        className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${tab === 'devis' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                        data-testid="tab-devis"
                    >
                        Modifications devis <span className="text-gray-400">({devisItems.length})</span>
                    </button>
                </div>

                {loading ? (
                    <div className="py-10 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                ) : tab === 'prospect' ? (
                    items.length === 0 ? (
                        <div className="py-10 text-center text-sm text-gray-500" data-testid="historique-empty">
                            Aucune modification enregistrée pour le moment.
                        </div>
                    ) : (
                        <ol className="relative border-l-2 border-gray-200 ml-3 mt-2 space-y-4" data-testid="historique-list">
                            {items.map((h, i) => (
                                <li key={i} className="ml-5" data-testid={`historique-item-${i}`}>
                                    <span className="absolute -left-[7px] mt-1 flex h-3 w-3 items-center justify-center rounded-full bg-[#C9A962] ring-4 ring-white" />
                                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                                            <span className="font-semibold text-gray-700">{fmt(h.changed_at)}</span>
                                            <span>·</span>
                                            <span className="inline-flex items-center gap-1">
                                                <User className="w-3 h-3" />
                                                <span className="font-medium text-[#1B365D]">{h.changed_by_name || 'Inconnu'}</span>
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <StatutBadge value={h.from_status} />
                                            <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                                            <StatutBadge value={h.to_status} />
                                        </div>
                                        {h.remarque && (
                                            <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-600 bg-white rounded p-2 border border-gray-100">
                                                <MessageSquare className="w-3 h-3 mt-0.5 text-gray-400 shrink-0" />
                                                <span>{h.remarque}</span>
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )
                ) : (
                    devisItems.length === 0 ? (
                        <div className="py-10 text-center text-sm text-gray-500" data-testid="devis-history-empty">
                            <FileText className="w-9 h-9 mx-auto mb-2 text-gray-300" />
                            Aucune modification de devis enregistrée pour le moment.
                        </div>
                    ) : (
                        <ol className="relative border-l-2 border-gray-200 ml-3 mt-2 space-y-4" data-testid="devis-history-list">
                            {devisItems.map((h, i) => {
                                const meta = ACTION_META[h.action] || { label: h.action, icon: FileEdit, accent: 'text-gray-600' };
                                const Icon = meta.icon;
                                return (
                                    <li key={i} className="ml-5" data-testid={`devis-history-item-${i}`}>
                                        <span className="absolute -left-[7px] mt-1 flex h-3 w-3 items-center justify-center rounded-full bg-[#1B365D] ring-4 ring-white" />
                                        <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-1">
                                                <Icon className={`w-3.5 h-3.5 ${meta.accent}`} />
                                                <span className={`font-semibold ${meta.accent}`}>{meta.label}</span>
                                                <span>·</span>
                                                <span className="font-semibold text-gray-700">{fmt(h.at)}</span>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1">
                                                    <User className="w-3 h-3" />
                                                    <span className="font-medium text-[#1B365D]">{h.by_name || 'Inconnu'}</span>
                                                </span>
                                            </div>
                                            <DevisActionDetails action={h.action} details={h.details} />
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )
                )}

                <div className="flex justify-end pt-2">
                    <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Fermer</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
